import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ordersAPI } from "../../../services/api";
import { CreateOrderRequest, OrderStatus } from "../../../types";

interface UseOrdersOptions {
  status?: string;
  salesperson_id?: string;
  page?: number;
  limit?: number;
}

interface UpdateOrderStatusParams {
  orderId: string;
  status: OrderStatus;
  notes?: string;
}

export const useOrders = (options: UseOrdersOptions = {}) => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState(options);

  const {
    data: ordersResponse,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["orders", filters],
    queryFn: async () => {
      return await ordersAPI.getOrders(filters);
    },
    staleTime: 30 * 1000, // 30 seconds (daha kısa)
    refetchInterval: 60 * 1000, // Auto-refresh every minute
    refetchOnWindowFocus: true, // Pencere odaklandığında güncelle
    refetchOnMount: true, // Component mount olduğunda güncelle
  });

  const createOrderMutation = useMutation({
    mutationFn: async (orderData: CreateOrderRequest) => {
      return await ordersAPI.createOrder(orderData);
    },
    onSuccess: () => {
      // Invalidate all related queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });
      queryClient.invalidateQueries({ queryKey: ["recent-orders"] });
      queryClient.invalidateQueries({ queryKey: ["my-orders"] });
    },
  });

  const updateOrderStatusMutation = useMutation({
    mutationFn: async ({ orderId, status, notes }: UpdateOrderStatusParams) => {
      return await ordersAPI.updateOrderStatus(orderId, { status, notes });
    },
    onSuccess: () => {
      // Invalidate all related queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });
      queryClient.invalidateQueries({ queryKey: ["recent-orders"] });
      queryClient.invalidateQueries({ queryKey: ["my-orders"] });
    },
  });

  const assignDriverMutation = useMutation({
    mutationFn: async ({ orderId, driverId }: { orderId: string; driverId: string }) => {
      return await ordersAPI.assignDriver(orderId, { driver_id: driverId });
    },
    onSuccess: () => {
      // Invalidate all related queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });
      queryClient.invalidateQueries({ queryKey: ["recent-orders"] });
      queryClient.invalidateQueries({ queryKey: ["my-orders"] });
    },
  });

  const updateFilters = useCallback((newFilters: Partial<UseOrdersOptions>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const createOrder = useCallback(
    async (orderData: CreateOrderRequest) => {
      return await createOrderMutation.mutateAsync(orderData);
    },
    [createOrderMutation]
  );

  const updateOrderStatus = useCallback(
    async (orderId: string, status: OrderStatus, notes?: string) => {
      return await updateOrderStatusMutation.mutateAsync({ orderId, status, notes });
    },
    [updateOrderStatusMutation]
  );

  const assignDriver = useCallback(
    async (orderId: string, driverId: string) => {
      return await assignDriverMutation.mutateAsync({ orderId, driverId });
    },
    [assignDriverMutation]
  );

  return {
    orders: ordersResponse?.orders || [],
    total: ordersResponse?.pagination?.total || 0,
    isLoading,
    error,
    refetch,
    filters,
    updateFilters,
    createOrder,
    updateOrderStatus,
    assignDriver,
    isCreating: createOrderMutation.isPending,
    isUpdating: updateOrderStatusMutation.isPending,
    isAssigning: assignDriverMutation.isPending,
  };
};

export const useMyOrders = () => {
  const queryClient = useQueryClient();

  const {
    data: ordersResponse,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["my-orders"],
    queryFn: async () => {
      return await ordersAPI.getOrdersByRole("salesperson");
    },
    staleTime: 30 * 1000, // 30 seconds (daha kısa)
    refetchInterval: 60 * 1000, // Auto-refresh every minute
    refetchOnWindowFocus: true, // Pencere odaklandığında güncelle
    refetchOnMount: true, // Component mount olduğunda güncelle
  });

  const createOrderMutation = useMutation({
    mutationFn: async (orderData: CreateOrderRequest) => {
      return await ordersAPI.createOrder(orderData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-orders"] });
    },
  });

  return {
    orders: ordersResponse || [],
    total: ordersResponse?.length || 0,
    isLoading,
    error,
    refetch,
    createOrder: createOrderMutation.mutateAsync,
    isCreating: createOrderMutation.isPending,
  };
};

export const useOrder = (orderId: string) => {
  const queryClient = useQueryClient();

  const {
    data: order,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["order", orderId],
    queryFn: async () => {
      return await ordersAPI.getOrder(orderId);
    },
    enabled: !!orderId,
    staleTime: 30 * 1000, // 30 seconds (daha kısa)
    refetchInterval: 60 * 1000, // Auto-refresh every minute
    refetchOnWindowFocus: true, // Pencere odaklandığında güncelle
    refetchOnMount: true, // Component mount olduğunda güncelle
  });

  const updateOrderStatusMutation = useMutation({
    mutationFn: async ({ status, notes }: { status: OrderStatus; notes?: string }) => {
      return await ordersAPI.updateOrderStatus(orderId, { status, notes });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["order", orderId] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });

  return {
    order,
    isLoading,
    error,
    refetch,
    updateOrderStatus: updateOrderStatusMutation.mutateAsync,
    isUpdating: updateOrderStatusMutation.isPending,
  };
}; 